import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { guilds } from "../data";
import { formatNumber } from "../utils/format";

const pillars = [
  {
    title: "Verified ecosystems",
    copy: "Every listing is reviewed for active programming, clear governance, and transparent onboarding.",
    tone: "sun",
    icon: "shield"
  },
  {
    title: "Cross-platform snapshots",
    copy: "Discord, Telegram, Slack, Reddit, sites, and IRL meetups sit in one calm dossier per guild.",
    tone: "mint",
    icon: "layers"
  },
  {
    title: "Map-native discovery",
    copy: "Leaflet-powered map and mini-maps deliver the spatial context and filters you actually need.",
    tone: "coral",
    icon: "map"
  }
];

const heroMetrics = [
  { label: "Guild dossiers", value: "34" },
  { label: "Communities tracked", value: "212" },
  { label: "Platforms monitored", value: "11" }
];

const partnerSignals = [
  "Research labs",
  "Studio scouts",
  "University innovation teams",
  "Brand partnerships",
  "Ecosystem builders",
  "Civic tech collectives",
  "Creative residencies",
  "Gaming alliances"
];

const workflowSteps = [
  {
    title: "Curation intake",
    detail: "Editors audit governance, leadership transparency, and cadence before anything touches the JSON layer."
  },
  {
    title: "Map calibration",
    detail: "Each community gets coordinates, tags, cadence, and platform notes so Leaflet clusters stay meaningful."
  },
  {
    title: "Living updates",
    detail: "Quarterly check-ins and field notes keep dossiers accurate for operations teams and scouts."
  }
];

const modules = [
  {
    title: "Guild dossiers",
    copy: "Concise snapshots covering mission, cadence, governance, and links.",
    icon: "layers"
  },
  {
    title: "Atlas briefings",
    copy: "Shortlists for scouts or partnerships teams, based on verified metadata.",
    icon: "compass"
  },
  {
    title: "Mini-maps",
    copy: "Guild-specific Leaflet maps with tag, platform, and regional filters.",
    icon: "map"
  },
  {
    title: "Submission runway",
    copy: "Guided intake for guild stewards to submit evidence and coordinates.",
    icon: "shield"
  }
];

const journeys = [
  {
    title: "Builder alliances",
    detail: "AI labs, indie hackers, and automation guilds sharing applied knowledge.",
    icon: "layers"
  },
  {
    title: "Culture & craft",
    detail: "Studios, design residencies, and creative collectives powering human experiences.",
    icon: "compass"
  },
  {
    title: "IRL & local",
    detail: "Neighborhood clubs, civic labs, and IRL fellowships tied to digital guilds.",
    icon: "map"
  }
];

const caseStudies = [
  {
    title: "AI Builders",
    summary: "Identified six cross-platform pods running consistent office hours, unlocking intros in under a week.",
    stat: "6 pods surfaced",
    guildId: "ai-builders"
  },
  {
    title: "Designers & Creatives",
    summary: "Mapped creative residencies across continents and delivered a shortlist of IRL-friendly partners.",
    stat: "12 residencies verified",
    guildId: "designers-creatives"
  }
];

const experienceStrips = [
  {
    title: "Curator lanes",
    metric: "12 curators",
    copy: "Humans interview guild leads weekly to confirm cadence and governance."
  },
  {
    title: "Verified nodes",
    metric: "212 listings",
    copy: "Every entry carries coordinates, tags, and cross-platform context."
  },
  {
    title: "Briefings shipped",
    metric: "48 in Q1",
    copy: "Stakeholders receive themed packs with contact intel and program notes."
  }
];

const storyMoments = [
  {
    title: "Scouting view",
    detail: "Leaflet overlays highlight active IRL nodes and travel-ready programs.",
    icon: "map"
  },
  {
    title: "Signal board",
    detail: "Editorial notes capture cadence shifts, platform migrations, and governance updates.",
    icon: "layers"
  },
  {
    title: "Submission runway",
    detail: "Guild leads upload code of conduct, onboarding flows, and geodata for fast verification.",
    icon: "shield"
  }
];

const Home = () => {

  const featuredGuilds = guilds.filter((guild) => guild.featured).slice(0, 3);

  return (
    <div className="space-y-12">
      <section className="panel hero-grid text-center">
        <span className="hero-ornament hero-ornament--left" aria-hidden="true"></span>
        <span className="hero-ornament hero-ornament--right" aria-hidden="true"></span>
        <div className="mx-auto max-w-3xl space-y-6 text-center">
          <p className="eyebrow">Overview</p>
          <div className="space-y-4">
            <h1 className="text-4xl leading-tight text-slate-100 sm:text-5xl">
              Discover cross-platform guilds without the noise.
            </h1>
            <p className="text-lg text-slate-200">
              Member Discovery keeps only verified ecosystems and presents them with calm typography,
              precise filters, and Leaflet-native maps so research teams can trust every snapshot.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 text-sm">
            <Link to="/directory" className="btn-primary">
              Browse guild directory
            </Link>
            <Link to="/map" className="btn-secondary">
              Open global map
            </Link>
          </div>
          <div className="grid gap-4 rounded-3xl border border-brand-ink/40 bg-brand-night/70 p-5 text-center text-sm text-brand-steel sm:grid-cols-3">
            {heroMetrics.map((metric) => (
              <div key={metric.label}>
                <p className="text-3xl font-semibold text-slate-100">{metric.value}</p>
                <p className="text-xs uppercase tracking-[0.3em]">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-wrapper">
        <div className="signal-row" aria-label="Signals">
          <div className="signal-row__track">
            {[...partnerSignals, ...partnerSignals].map((signal, index) => (
              <span key={`${signal}-${index}`} className="signal-row__pill">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-primary" />
                {signal}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="section-wrapper">
        <section className="space-y-8">
          <div className="section-heading section-heading--left">
            <p className="eyebrow">Foundation</p>
            <h2 className="text-3xl text-slate-100">Foundation</h2>
            <p className="text-sm text-slate-400">Standards, metrics, and tooling keep the atlas precise.</p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <article className="panel space-y-4">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">Principles</p>
                <p className="text-sm text-slate-400">Review checkpoints before a guild enters the atlas.</p>
              </div>
              <div className="grid gap-3">
                {pillars.map((item) => (
                  <div key={item.title} className="foundation-principle">
                    <div className="foundation-principle__icon">
                      <Icon name={item.icon} className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-base text-slate-100">{item.title}</h3>
                      <p className="text-sm text-slate-300">{item.copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </article>
            <article className="panel space-y-4">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">Systems</p>
                <p className="text-sm text-slate-400">Tools surfaced the most by partnerships and research teams.</p>
              </div>
              <div className="space-y-4">
                {modules.map((module) => (
                  <div key={module.title} className="flex items-start gap-3 rounded-2xl border border-brand-ink/40 bg-brand-night/70 p-3">
                    <span className="icon-badge icon-badge--primary">
                      <Icon name={module.icon} />
                    </span>
                    <div>
                      <h3 className="text-lg text-slate-100">{module.title}</h3>
                      <p className="text-sm text-slate-300">{module.copy}</p>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {experienceStrips.map((item) => (
              <article key={item.title} className="experience-strip wow-glow">
                <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">{item.title}</p>
                <p className="experience-strip__metric">{item.metric}</p>
                <p>{item.copy}</p>
              </article>
            ))}
          </div>
        </section>
      </div>

      <div className="section-wrapper">
        <section className="space-y-5">
          <div className="space-y-2">
            <p className="eyebrow">Spotlights</p>
            <h2 className="text-3xl text-slate-100">Spotlights</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {featuredGuilds.map((guild, index) => (
              <FeaturedGuildCard key={guild.id} guild={guild} index={index} />
            ))}
          </div>
        </section>
      </div>

      <div className="section-wrapper">
        <section className="space-y-4">
          <div className="section-heading section-heading--left">
            <p className="eyebrow">Experience</p>
            <h2 className="text-3xl text-slate-100">Experience</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {storyMoments.map((moment) => (
              <article key={moment.title} className="story-card">
                <span className="icon-badge icon-badge--primary">
                  <Icon name={moment.icon} />
                </span>
                <h3>{moment.title}</h3>
                <p>{moment.detail}</p>
              </article>
            ))}
          </div>
        </section>
      </div>

      <div className="section-wrapper">
        <section className="panel space-y-5">
          <div className="space-y-2">
            <p className="eyebrow">Workflow</p>
            <h2 className="text-3xl text-slate-100">Workflow</h2>
            <p className="text-sm text-slate-400">Curators verify each guild with interviews and proof.</p>
          </div>
          <ol className="grid gap-4 text-sm text-slate-300 md:grid-cols-3">
            {workflowSteps.map((step, index) => (
              <li key={step.title} className="timeline-step pl-6">
                <p className="text-xs uppercase tracking-[0.45em] text-brand-steel">Step {index + 1}</p>
                <h3 className="mt-2 text-xl text-slate-100">{step.title}</h3>
                <p className="mt-2 text-slate-300">{step.detail}</p>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <div className="section-wrapper">
        <section className="space-y-5">
          <div className="space-y-2">
            <p className="eyebrow">Journeys</p>
            <h2 className="text-3xl text-slate-100">Journeys</h2>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {journeys.map((journey) => (
              <article key={journey.title} className="accent-card space-y-3 wow-glow">
                <span className="icon-badge icon-badge--primary">
                  <Icon name={journey.icon} />
                </span>
                <h3 className="text-xl text-slate-100">{journey.title}</h3>
                <p className="text-sm text-slate-300">{journey.detail}</p>
                <Link to="/directory" className="text-xs uppercase tracking-[0.3em] text-brand-secondary">
                  Explore
                </Link>
              </article>
            ))}
          </div>
        </section>
      </div>

      <div className="section-wrapper">
        <section className="space-y-5">
          <div className="space-y-2">
            <p className="eyebrow">Briefs</p>
            <h2 className="text-3xl text-slate-100">Briefs</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {caseStudies.map((study) => (
              <article key={study.title} className="case-card">
                <p className="text-xs uppercase tracking-[0.3em] text-brand-steel">Guild case study</p>
                <h3 className="text-2xl text-slate-100">{study.title}</h3>
                <p className="text-sm text-slate-300">{study.summary}</p>
                <p className="text-xs uppercase tracking-[0.3em] text-brand-secondary">{study.stat}</p>
                <div className="flex flex-wrap gap-3 text-sm">
                  <Link to={`/guilds/${study.guildId}`} className="btn-primary">
                    View dossier
                  </Link>
                  <Link to={`/guilds/${study.guildId}/map`} className="btn-secondary">
                    Mini-map
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>
      </div>

      <div className="section-wrapper">
        <section className="cta-panel">
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-[0.45em] text-slate-300">Next steps</p>
            <h2 className="text-3xl font-display text-slate-100">Next steps</h2>
            <p className="text-sm text-slate-200">
              Share your ecosystem or ask for a guided briefing. We’ll reply with verification criteria and next steps.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to="/submit" className="btn-primary">
              Submit a guild
            </Link>
            <Link to="/about" className="btn-secondary">
              Review curation standards
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

const FeaturedGuildCard = ({ guild, index }) => (
  <article className="flex flex-col gap-4 rounded-[26px] border border-brand-ink/50 bg-brand-night/85 p-5 shadow-[0_12px_30px_rgba(0,0,0,0.4)] transition hover:-translate-y-1 hover:border-brand-primary/50">
    <header className="flex items-center justify-between text-[11px] uppercase tracking-[0.35em] text-brand-steel">
      <span>Spotlight {index + 1}</span>
      <span>{guild.regionFocus}</span>
    </header>
    <div className="flex items-start gap-4">
      <span
        className="grid h-14 w-14 place-content-center rounded-2xl border border-brand-ink/60 bg-brand-midnight text-sm font-semibold text-slate-100"
        style={{ color: guild.accentColor }}
      >
        {guild.logo}
      </span>
      <div className="space-y-1">
        <p className="text-[11px] uppercase tracking-[0.3em] text-brand-steel">{guild.category}</p>
        <h3 className="text-2xl text-slate-100">{guild.name}</h3>
        <p className="text-sm text-slate-400">{guild.tagline}</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-2 text-xs text-brand-steel">
      <span className="chip chip-soft">{guild.regionFocus}</span>
      <span className="chip chip-soft">Founded {guild.founded}</span>
      {guild.tags.slice(0, 1).map((tag) => (
        <span key={tag} className="chip chip-soft">
          {tag}
        </span>
      ))}
    </div>
    <dl className="grid gap-3 rounded-2xl border border-brand-ink/40 bg-brand-midnight/80 p-3 text-sm text-slate-200 sm:grid-cols-3">
      <div>
        <dt className="text-[11px] uppercase tracking-[0.3em] text-brand-steel">Members</dt>
        <dd className="text-lg font-semibold text-brand-highlight">{formatNumber(guild.memberCount)}</dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-[0.3em] text-brand-steel">Communities</dt>
        <dd className="text-lg font-semibold text-brand-highlight">{guild.communitiesCount}</dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-[0.3em] text-brand-steel">Platforms</dt>
        <dd className="text-lg font-semibold text-brand-highlight">{guild.platforms.length}</dd>
      </div>
    </dl>
    <div className="flex flex-wrap gap-3 text-sm">
      <Link to={`/guilds/${guild.id}`} className="btn-primary">
        View guild
      </Link>
      <Link to={`/guilds/${guild.id}/map`} className="btn-secondary">
        Mini-map
      </Link>
    </div>
  </article>
);

export default Home;
